import { useLocation, useRouter } from '@tanstack/react-router'
import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import {
  getPersistedTabState,
  persistTabState,
} from 'features/knowledgeDocumentViewer/utils/documentPageSessionStorage'
import {
  createBrowseAllDocumentsTab,
  createDestinationTab,
  createWorkspaceHomeTab,
} from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import { SelectedTab } from 'features/navbar/types'
import {
  chartLocalStateKey,
  clearChartLocalState,
} from 'features/timeSeries/utils/chartLocalState'
import React, {
  createContext,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'

import { getWorkspaceTabPathname } from './getWorkspaceTabPathname'
import { isReusableDocumentsIndexTab } from './isReusableDocumentsIndexTab'
import { navigateToWorkspaceTab } from './navigateToWorkspaceTab'
import { scheduleAfterPaint } from './scheduleAfterPaint'
import { useSyncActiveDestinationHref } from './useSyncActiveDestinationHref'
import { useSyncBrowserDocumentTitle } from './useSyncBrowserDocumentTitle'
import { useWorkspaceTabPersistence } from './useWorkspaceTabPersistence'

interface OpenDestinationTabOptions {
  destinationKey: string
  label: string
  href: string
}

interface WorkspaceTabsState {
  openTabs: DocumentTab[]
  activeTabId: string
}

interface WorkspaceTabsActions {
  setOpenTabs: Dispatch<SetStateAction<DocumentTab[]>>
  setActiveTabId: Dispatch<SetStateAction<string>>
  setWorkspaceScope: (scope: string) => void
  openDestinationTab: (options: OpenDestinationTabOptions) => void
  openDefaultTab: () => void
  setActiveTabTitle: (title: string | null) => void
  activateWorkspaceTab: (tabId: string) => void
  closeWorkspaceTab: (tabId: string) => void
}

export type WorkspaceTabsContextValue = WorkspaceTabsState &
  WorkspaceTabsActions

const WorkspaceTabsStateContext = createContext<WorkspaceTabsState | null>(null)
const WorkspaceTabsActionsContext = createContext<WorkspaceTabsActions | null>(
  null
)

const getScopeMode = (scope: string): 'active' | 'preview' =>
  scope.endsWith(':preview') ? 'preview' : 'active'

const getInitialState = (scope: string) => {
  const persisted = getPersistedTabState(scope)
  if (persisted) {
    return {
      openTabs: persisted.openTabs,
      activeTabId: persisted.activeTabId,
    }
  }

  const defaultTab = createWorkspaceHomeTab()
  return { openTabs: [defaultTab], activeTabId: defaultTab.id }
}

export function WorkspaceTabsProvider({
  siteId,
  children,
}: {
  siteId: string
  children: React.ReactNode
}) {
  const router = useRouter()
  const location = useLocation()
  const initialScope = `${siteId}:active`
  const [scope, setScope] = useState(initialScope)
  const [initialState] = useState(() => getInitialState(initialScope))
  const [openTabs, setOpenTabs] = useState(initialState.openTabs)
  const [activeTabId, setActiveTabId] = useState(initialState.activeTabId)
  const scopeRef = useRef(scope)
  const openTabsRef = useRef(openTabs)
  const activeTabIdRef = useRef(activeTabId)

  scopeRef.current = scope
  openTabsRef.current = openTabs
  activeTabIdRef.current = activeTabId

  useSyncActiveDestinationHref({
    locationHref: location.href,
    setOpenTabs,
    activeTabIdRef,
  })

  // Site swaps keep preview/active mode; setWorkspaceScope owns mode changes.
  useEffect(() => {
    const nextScope = `${siteId}:${getScopeMode(scopeRef.current)}`
    if (scopeRef.current === nextScope) return

    persistTabState(
      scopeRef.current,
      openTabsRef.current,
      activeTabIdRef.current
    )
    const nextState = getInitialState(nextScope)
    scopeRef.current = nextScope
    setScope(nextScope)
    setOpenTabs(nextState.openTabs)
    setActiveTabId(nextState.activeTabId)
  }, [siteId])

  useWorkspaceTabPersistence({
    scope,
    openTabs,
    activeTabId,
    scopeRef,
    openTabsRef,
    activeTabIdRef,
  })

  useSyncBrowserDocumentTitle(location.href, activeTabId)

  const setWorkspaceScope = useCallback((nextScope: string) => {
    if (scopeRef.current === nextScope) return

    persistTabState(
      scopeRef.current,
      openTabsRef.current,
      activeTabIdRef.current
    )
    const nextState = getInitialState(nextScope)
    scopeRef.current = nextScope
    setScope(nextScope)
    setOpenTabs(nextState.openTabs)
    setActiveTabId(nextState.activeTabId)
  }, [])

  const navigateToTab = useCallback(
    (tab: DocumentTab) => {
      navigateToWorkspaceTab(tab, {
        historyPush: (href) => {
          router.history.push(href)
        },
        navigateToDocuments: (options) => router.navigate(options),
      })
    },
    [router]
  )

  const activateWorkspaceTab = useCallback(
    (tabId: string) => {
      const tab = openTabsRef.current.find(
        (candidate) => candidate.id === tabId
      )
      if (!tab) return

      // Paint the selected tab before starting route work.
      flushSync(() => {
        setActiveTabId(tabId)
      })
      scheduleAfterPaint(() => navigateToTab(tab))
    },
    [navigateToTab]
  )

  const openDestinationTab = useCallback(
    (options: OpenDestinationTabOptions) => {
      const requestedPathname = getWorkspaceTabPathname(options.href)
      // Home always opens a fresh landing tab (same as +). Documents reuses a
      // pristine browse-all index; other destinations reuse matching tabs.
      const existingIndexTab =
        options.destinationKey === SelectedTab.Home
          ? undefined
          : openTabsRef.current.find((tab) => {
              if (options.destinationKey === SelectedTab.Documents) {
                return isReusableDocumentsIndexTab(tab)
              }

              const item = tab.history[tab.currentIndex]
              return (
                item.type === 'destination' &&
                item.destinationKey === options.destinationKey &&
                getWorkspaceTabPathname(item.href) === requestedPathname
              )
            })

      if (existingIndexTab) {
        activateWorkspaceTab(existingIndexTab.id)
        return
      }

      const tab =
        options.destinationKey === SelectedTab.Home
          ? createWorkspaceHomeTab()
          : options.destinationKey === SelectedTab.Documents
            ? createBrowseAllDocumentsTab()
            : createDestinationTab(options)
      flushSync(() => {
        setOpenTabs((currentTabs) => [...currentTabs, tab])
        setActiveTabId(tab.id)
      })
      scheduleAfterPaint(() => navigateToTab(tab))
    },
    [activateWorkspaceTab, navigateToTab]
  )

  const openDefaultTab = useCallback(() => {
    const tab = createWorkspaceHomeTab()
    flushSync(() => {
      setOpenTabs((currentTabs) => [...currentTabs, tab])
      setActiveTabId(tab.id)
    })
    scheduleAfterPaint(() => {
      void router.navigate({ to: '/documents' })
    })
  }, [router])

  const setActiveTabTitle = useCallback((title: string | null) => {
    const nextLabel = title === null ? undefined : title
    setOpenTabs((currentTabs) => {
      let changed = false
      const nextTabs = currentTabs.map((tab) => {
        if (
          tab.id !== activeTabIdRef.current ||
          tab.customLabel === nextLabel
        ) {
          return tab
        }
        changed = true
        return { ...tab, customLabel: nextLabel }
      })
      return changed ? nextTabs : currentTabs
    })
  }, [])

  const closeWorkspaceTab = useCallback(
    (tabId: string) => {
      const currentTabs = openTabsRef.current
      const closingIndex = currentTabs.findIndex((tab) => tab.id === tabId)
      if (closingIndex === -1) return

      const closingTab = currentTabs[closingIndex]
      if (closingTab?.closable === false) return

      // Same cleanup as legacy removeTab: discard orphaned chart local state.
      clearChartLocalState(chartLocalStateKey(siteId, tabId))

      if (currentTabs.length === 1) {
        const defaultTab = createWorkspaceHomeTab()
        flushSync(() => {
          setOpenTabs([defaultTab])
          setActiveTabId(defaultTab.id)
        })
        scheduleAfterPaint(() => navigateToTab(defaultTab))
        return
      }

      const nextTabs = currentTabs.filter((tab) => tab.id !== tabId)
      const shouldActivateNext = activeTabIdRef.current === tabId
      const nextTab = shouldActivateNext
        ? (nextTabs[Math.min(closingIndex, nextTabs.length - 1)] ?? nextTabs[0])
        : undefined

      flushSync(() => {
        setOpenTabs(nextTabs)
        if (nextTab) {
          setActiveTabId(nextTab.id)
        }
      })
      if (nextTab) {
        scheduleAfterPaint(() => navigateToTab(nextTab))
      }
    },
    [navigateToTab, siteId]
  )

  const state = useMemo<WorkspaceTabsState>(
    () => ({
      openTabs,
      activeTabId,
    }),
    [activeTabId, openTabs]
  )

  const actions = useMemo<WorkspaceTabsActions>(
    () => ({
      setOpenTabs,
      setActiveTabId,
      setWorkspaceScope,
      openDestinationTab,
      openDefaultTab,
      setActiveTabTitle,
      activateWorkspaceTab,
      closeWorkspaceTab,
    }),
    [
      activateWorkspaceTab,
      closeWorkspaceTab,
      openDefaultTab,
      openDestinationTab,
      setActiveTabTitle,
      setWorkspaceScope,
    ]
  )

  return (
    <WorkspaceTabsActionsContext.Provider value={actions}>
      <WorkspaceTabsStateContext.Provider value={state}>
        {children}
      </WorkspaceTabsStateContext.Provider>
    </WorkspaceTabsActionsContext.Provider>
  )
}

export const useOptionalWorkspaceTabsActions = () =>
  useContext(WorkspaceTabsActionsContext)

export const useOptionalWorkspaceTabsState = () =>
  useContext(WorkspaceTabsStateContext)

/** Combined optional access for dual-path consumers (e.g. useTabState). */
export const useOptionalWorkspaceTabs =
  (): WorkspaceTabsContextValue | null => {
    const state = useOptionalWorkspaceTabsState()
    const actions = useOptionalWorkspaceTabsActions()
    return useMemo(() => {
      if (!state || !actions) return null
      return { ...state, ...actions }
    }, [actions, state])
  }

export const useWorkspaceTabs = (): WorkspaceTabsContextValue => {
  const context = useOptionalWorkspaceTabs()
  if (!context) {
    throw new Error(
      'useWorkspaceTabs must be used within a WorkspaceTabsProvider'
    )
  }
  return context
}
